import { createPortal } from "react-dom";
import { useToastList, type Toast, type ToastTone } from "./ToastContext";

/**
 * Visual layer for the toast system.
 *
 * `<ToastViewport>` renders the live toast stack in a fixed corner,
 * portaled to `document.body` so it floats above every surface —
 * including the Coach Review composer drawer and focus-mode overlay
 * (which themselves portal at `z-[140]`-ish; the viewport sits at
 * `z-[200]`).
 *
 * Styling reuses existing tokens only — `good` (success),
 * `destructive` (error), `accent` (info), `surface` / `rule` / `ink`
 * for the card — so no new design tokens are introduced. Icons are
 * inline SVG (the repo has no icon dependency).
 *
 * Accessibility: the viewport is an `aria-live` region. Success /
 * info post politely; errors assert so a screen reader announces a
 * failed save promptly.
 */

const TONE_STYLES: Record<
  ToastTone,
  { accent: string; iconWrap: string }
> = {
  success: { accent: "border-l-good", iconWrap: "text-good" },
  error: { accent: "border-l-destructive", iconWrap: "text-destructive" },
  info: { accent: "border-l-accent", iconWrap: "text-accent" },
};

function ToneIcon({ tone }: { tone: ToastTone }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 20 20",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (tone === "success") {
    return (
      <svg {...common}>
        <circle cx="10" cy="10" r="8" />
        <path d="M6.5 10.5l2.5 2.5 4.5-5" />
      </svg>
    );
  }
  if (tone === "error") {
    return (
      <svg {...common}>
        <circle cx="10" cy="10" r="8" />
        <path d="M10 6v5M10 14v.01" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="10" cy="10" r="8" />
      <path d="M10 9v5M10 6v.01" />
    </svg>
  );
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const tone = TONE_STYLES[toast.tone];
  return (
    <div
      // Errors assert; success / info post politely.
      role={toast.tone === "error" ? "alert" : "status"}
      data-testid="toast"
      data-toast-tone={toast.tone}
      className={[
        "pointer-events-auto flex w-80 items-start gap-2.5 rounded-md border border-rule border-l-[3px]",
        "bg-elevated px-3 py-2.5 shadow-lg",
        tone.accent,
      ].join(" ")}
    >
      <span className={["mt-px flex-shrink-0", tone.iconWrap].join(" ")}>
        <ToneIcon tone={toast.tone} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-semibold text-ink">
          {toast.message}
        </p>
        {toast.description ? (
          <p className="mt-0.5 break-words text-xs text-ink-muted">
            {toast.description}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        data-testid="toast-dismiss"
        className="-mr-1 -mt-0.5 flex-shrink-0 rounded p-1 text-ink-muted transition-colors hover:bg-surface hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <svg
          width={14}
          height={14}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          aria-hidden
        >
          <path d="M5 5l10 10M15 5L5 15" />
        </svg>
      </button>
    </div>
  );
}

export function ToastViewport() {
  const { toasts, dismiss } = useToastList();
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[200] flex flex-col gap-2"
      aria-live="polite"
      aria-atomic="false"
      data-testid="toast-viewport"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={dismiss} />
      ))}
    </div>,
    document.body,
  );
}
